class CarouselAnalyticsTracker {
    constructor() {
        this.seenLists = new Set();
        this.observer = null;
        this.mutationObserver = null;
    }

    getListPayload(listId) {
        const script = document.querySelector(`script[data-tracking-list-id="${listId}"]`);
        if (!script) return null;

        try {
            const data = JSON.parse(script.textContent);
            if (!Array.isArray(data.items)) return null;

            return {
                item_list_id: listId,
                item_list_name: data.list_name || listId,
                items: data.items
            };
        } catch (e) {
            console.warn(`Invalid JSON in carousel script for "${listId}"`, e);
            return null;
        }
    }

    publishViewItemList(payload) {
        if (typeof window.Shopify?.analytics?.publish === 'function') {
            window.Shopify.analytics.publish('forix:item_list', payload);
        } else {
            console.warn('Shopify analytics not ready.');
        }
    }

    observeCarousels() {
        
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (!entry.isIntersecting) return;

                const el = entry.target;
                const listId = el.dataset.trackingListId;
                if (!listId || this.seenLists.has(listId)) return;

                const payload = this.getListPayload(listId);
                if (payload) {
                    this.publishViewItemList(payload);
                    this.seenLists.add(listId);
                }
            });
        }, { threshold: 0 });

        // Observe existing elements
        this.observeExistingElements();

        // Start watching for new elements
        this.startMutationObserver();
    }

    observeExistingElements() {
        document.querySelectorAll('div[data-tracking-list-id], product-list[data-tracking-list-id]').forEach(el => {
            this.observer.observe(el);
        });
    }

    startMutationObserver() {
        this.mutationObserver = new MutationObserver((mutations) => {
            mutations.forEach(mutation => {
                // Check for added nodes
                mutation.addedNodes.forEach(node => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        // Check if the added node itself has the attribute
                        if (node.hasAttribute && node.hasAttribute('data-tracking-list-id')) {
                            this.observeElement(node);
                        }

                        // Check children of the added node
                        const childrenWithTracking = node.querySelectorAll && node.querySelectorAll('div[data-tracking-list-id], product-list[data-tracking-list-id]');
                        if (childrenWithTracking) {
                            childrenWithTracking.forEach(child => {
                                this.observeElement(child);
                            });
                        }
                    }
                });
            });
        });

        // Start observing the entire document for changes
        this.mutationObserver.observe(document.body, {
            childList: true,
            subtree: true
        });
    }

    observeElement(el) {
        const listId = el.dataset.trackingListId;
        if (listId && !this.seenLists.has(listId)) {
            this.observer.observe(el);
        }
    }

    init() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.observeCarousels());
        } else {
            this.observeCarousels();
        }
    }

    destroy() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
            this.mutationObserver = null;
        }
        this.seenLists.clear();
    }
}

// Initialize the tracker
const carouselTracker = new CarouselAnalyticsTracker();
carouselTracker.init();

window.addEventListener('DOMContentLoaded', () => {
    function getQueryParam(param) {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(param);
    }
    const searchQuery = getQueryParam('_psq');
    const isSearchPage = window.location.pathname.includes('/search');
    if (searchQuery && !isSearchPage) {
        sendGAEvent('forix:autocomplete_search', {
            search_term: searchQuery,
        });
        sendGAEvent('view_search_results', {
            search_term: searchQuery,
        });
    }
    function sendGAEvent(eventName, eventData) {
        if (typeof window.Shopify?.analytics?.publish === 'function') {
            window.Shopify.analytics.publish(eventName, eventData);
        } else {
            console.warn('GA4 is not initialized.');
        }
    }
});

// ===== Frontend bundle publisher (Selleasy) =====

// Config
const BUNDLE_BTN_SELECTOR = '.lb-button-bundle';
const PUBLISH_DELAY_MS = 1000;   // wait for lines to be added
const PIXEL_PAUSE_MS   = 5000;  // pixel should skip line-level add_to_cart within this window

// Read cart
async function getCart() {
  const res = await fetch('/cart.js', { credentials: 'same-origin' });
  return res.json();
}

// GA4 item from cart line
function itemFromCartLine(line) {
  const priceNum = (Number(line?.final_line_price || 0) / 100) / (line?.quantity || 1);
  const vt = (line?.variant_title || '').toLowerCase();
  const vname = !vt || ['default','title','default title'].includes(vt) ? null : line.variant_title;

  return {
    item_id: line.sku || line.variant_id || line.product_id,
    item_name: line.product_title,
    item_brand: line.vendor,
    item_category: line.product_type,
    item_variant: vname,
    price: Number.isFinite(priceNum) ? +priceNum.toFixed(2) : undefined,
    quantity: line.quantity || 1,
  };
}

// Group by Selleasy key
function groupBySelleasyKey(lines) {
  const groups = new Map();
  for (const l of lines) {
    const key = l?.properties?.['_lb-product']
        ? String(l.properties['_lb-product'])
        : null;

    if (!key) continue;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(l);
  }
  return groups;
}

// De-dupe guard: remember recently published bundles
const publishedSignatures = new Set();
function makeSignature(bundleKey, lines) {
  const ids = lines.map(l => `${l.variant_id}:${l.quantity}`).sort().join('|');
  const val = lines.reduce((s, l) => s + Number(l.final_line_price || 0), 0); // in cents
  return `${bundleKey}::${ids}::${val}`;
}
function remember(sig) {
  publishedSignatures.add(sig);
  setTimeout(() => publishedSignatures.delete(sig), 60_000); // auto-expire after 1 min
}

// Publish ONE event per bundle (skip if already sent)
async function publishBundleOnce() {
  const cart = await getCart();
  const groups = groupBySelleasyKey(cart.items);

  for (const [bundleKey, lines] of groups) {
    const sig = makeSignature(bundleKey, lines);
    if (publishedSignatures.has(sig)) continue; // already sent

    const value = lines.reduce((sum, l) => sum + (Number(l.final_line_price || 0) / 100), 0);
    const items = lines.map(itemFromCartLine).filter(i => i.item_id || i.item_name);

    if (window.Shopify?.analytics?.publish) {
      window.Shopify.analytics.publish('forix:add_bundle', {
        bundle_key: bundleKey,
        currency: cart?.currency || 'USD',
        value: +value.toFixed(2),
        items,
      });
      remember(sig);
      // optional debug
      // console.debug('[forix:add_bundle] sent', { bundleKey, value, items });
    } else {
      console.warn('[forix:add_bundle] Shopify.analytics.publish not available.');
    }
  }
}

// Click hook
// document.addEventListener('click', (e) => {
//   const btn = e.target.closest(BUNDLE_BTN_SELECTOR);
//   if (!btn) return;

//   // Tell the pixel to pause line-level add_to_cart briefly (prevents double count)
//   if (window.Shopify?.analytics?.publish) {
//     window.Shopify.analytics.publish('forix:bundle_start', { ttl_ms: PIXEL_PAUSE_MS });
//   }
//   // After Selleasy adds lines, publish the grouped event
//   setTimeout(publishBundleOnce, PUBLISH_DELAY_MS);
// });
